import { useContext, useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { FunctionsContext, GlobalContext } from "../../common/Context";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ProfileCardMini from "../../common/ProfileCardMini";
import { getSingleDate } from "../../common/Utils/TimeUtils";
import MyTeams from "./components/DashboardBoxes/MyTeams";
import ActiveChallenges from "./components/DashboardBoxes/ActiveChallenges";
import RecentTryps from "./components/DashboardBoxes/RecentTryps";
import WeeklyVideo from "./components/DashboardBoxes/WeeklyVideo";
import SRCCard from "./components/DashboardBoxes/SRCCard";
import { ConstantNames } from "../../../constants";

const Dashboard = () => {
  const { t } = useTranslation(["Dashboard", "Navigation"]);
  const {
    mobileView,
    tabletView,
    desktopView,
    userState,
    firebaseState,
    setOptionState,
    userRankInfo,
    setUserRankInfo,
    userTopTryps,
    rankData,
  } = useContext(GlobalContext);
  const { getConstant } = useContext(FunctionsContext);
  const mainBanners = getConstant(ConstantNames.MAIN_BANNERS);
  const featuredBanners = getConstant(ConstantNames.FEATURED_BANNERS);

  const currentDate = moment();
  const week = useRef(currentDate.format("YYYY-WW"));
  const view = mobileView ? "mobile" : tabletView ? "tablet" : "desktop";

  const [avatarSource, setAvatarSource] = useState("");
  const [userTotal, setUserTotal] = useState();
  const [userGroups, setUserGroups] = useState([]);
  const [tryps, setTryps] = useState();
  const [activeCampaigns, setActiveCampaigns] = useState();
  const [votw, setVotw] = useState();
  const [userCompTeams, setUserCompTeams] = useState();

  const adBoxStyle = {
    width: "100%",
    height: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: "1rem",
  };
  const upperBoxStyle = {
    width: "100%",
    display: "flex",
    flexWrap: mobileView ? "wrap" : "nowrap",
    justifyContent: "space-between",
    mb: mobileView ? "1rem" : "2rem",
  };
  const appTitleAreaStyle = {
    width: tabletView ? "49%" : mobileView ? "100%" : "65%",
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    mb: mobileView && "1rem",
  };
  const appTitleStyle = {
    mb: "1rem",
    ...(tabletView && {
      fontSize: "1.25rem",
    }),
  };
  const appDescStyle = {
    lineHeight: "1.5",
    ...(tabletView && {
      fontSize: "0.875rem",
    }),
  };
  const middleAreaStyle = {
    width: "100%",
    display: "flex",
    flexWrap: mobileView ? "wrap" : "nowrap",
    justifyContent: "space-between",
    height: mobileView ? "100%" : tabletView ? "22rem" : "24rem",
    mb: !mobileView && "2rem",
  };
  const bottomAreaStyle = {
    width: "100%",
    display: "flex",
    flexWrap: !desktopView ? "wrap" : "nowrap",
    justifyContent: "space-between",
    height: mobileView ? "100%" : tabletView ? "auto" : "19rem",
  };
  const featChalBannerStyle = {
    cursor: activeCampaigns?.length && "pointer",
    width: "100%",
    height: "100%",
    borderRadius: desktopView ? "30px" : "20px",
  };

  useEffect(() => {
    const getVOTW = async () => {
      let result = await firebaseState.getFeaturedVideo();
      setVotw(result);
    };
    votw === undefined && getVOTW();
  }, [userState]);

  useEffect(() => {
    if (userState.avatar) {
      const typeNumber = Math.max(parseInt(userState.avatar.substring(4)), 12);
      setAvatarSource(`images/avatars/type${typeNumber || "12"}.svg`);
    }
  }, [userState]);

  useEffect(() => {
    try {
      const checkProfile = async () => {
        setTryps(userTopTryps.slice(0, 3));
      };
      userState?.profileID && userTopTryps && checkProfile();
    } catch (e) {
      console.error(e);
    }
  }, [userTopTryps, userState, mobileView]);

  useEffect(() => {
    const getCampaigns = async () => {
      if (!userState.teams?.length) {
        setActiveCampaigns({ error: t("errUserNoTeams") });
        return;
      }

      const today = getSingleDate(new Date(), false, false);
      const blacklistedTypes = ["weekImprovement", "pool"];

      let campaigns = [],
        actives = [];
      if (userState.isAdmin) {
        campaigns = await firebaseState.getAdminCampaigns(
          userState.teams.map((team) => team.id),
        );
      } else {
        campaigns = await firebaseState.getUserCampaigns(
          userState.profileID,
          userState.teams.map((team) => team.id),
        );
      }

      if (!campaigns?.length) {
        setActiveCampaigns({ error: t("errNoActiveChallenges") });
        return;
      }

      campaigns.forEach((campaign) => {
        if (
          campaign.end_date.value > today &&
          !blacklistedTypes.includes(campaign.campaign_type)
        ) {
          return (actives = [...actives, campaign]);
        }
      });

      const ids = actives
        .filter((ac) => ac?.prize_id && ac.prize_id !== "custom")
        .map((ac) => ac.prize_tier_id);
      const fullTiers = await firebaseState.getPrizeTiersByMultipleIds(ids);

      const activeCampaigns = actives.map((c) => {
        const tier = fullTiers.find((ft) => c.prize_tier_id === ft?.id);
        c.prizeDesc =
          c.prize_id == "custom" ? c.prize_tier_id : tier?.description;
        return c;
      });

      if (!activeCampaigns.length) {
        setActiveCampaigns({ error: t("errNoActiveChallenges") });
        return;
      }

      setActiveCampaigns(activeCampaigns);
    };

    userGroups &&
      userState.profileID &&
      activeCampaigns === undefined &&
      getCampaigns();
  }, [userState, userGroups, activeCampaigns]);

  useEffect(() => {
    try {
      const sortGroupRank = () => {
        let orgTeam = [
          {
            rank: rankData.orgRank?.rank,
            numUsers: rankData.orgRank?.numUsers,
            group: {
              id: rankData.orgRank?.orgId,
              name: "All Users",
            },
          },
        ];
        setUserRankInfo(rankData.orgRank);
        setUserGroups([...orgTeam, ...(rankData.groupRanks ?? [])]);
      };
      if (rankData) {
        sortGroupRank();
      }
    } catch (e) {
      setUserGroups([]);
      console.error(e.message);
    }
  }, [firebaseState, rankData]);

  useEffect(() => {
    const getUserTotal = async () => {
      let userScores = await firebaseState.getUserScoresInRange(
        week.current,
        week.current,
        userState.profileID,
      );
      userScores && setUserTotal(userScores.total);
    };
    userState?.profileID && getUserTotal();
  }, [userState.profileID, firebaseState]);

  const changeNav = (type) => {
    setOptionState({ nav: type, page: "main" });
  };

  useEffect(() => {
    const getUserStats = async () => {
      if (userState.profileID) {
        let userScores = await firebaseState.getUserCompetitionScores(
          userState.profileID,
          week.current,
        );
        setUserCompTeams(userScores);
      }
    };
    getUserStats();
  }, [userState]);

  return (
    <Box>
      <Box sx={adBoxStyle}>
        {mainBanners.data && (
          <Box
            component="img"
            alt={t("teamsIcon")}
            src={mainBanners.data[view]}
            onClick={() => activeCampaigns?.length && changeNav("safeRoads")}
            sx={featChalBannerStyle}
          />
        )}
      </Box>
      <Box sx={upperBoxStyle}>
        <Box sx={appTitleAreaStyle}>
          <Typography sx={appTitleStyle} variant="h1">
            {t("welcomeToApp")}
          </Typography>
          <Typography sx={appDescStyle} variant="body2">
            {t("dashboardDesc")}
          </Typography>
        </Box>
        <ProfileCardMini
          userTotal={userTotal}
          avatarSource={avatarSource}
          orgRank={userRankInfo}
        />
      </Box>
      <Box sx={middleAreaStyle}>
        <MyTeams userGroups={userGroups} />
        <ActiveChallenges activeCampaigns={activeCampaigns} />
      </Box>
      <Box sx={bottomAreaStyle}>
        <RecentTryps tryps={tryps} />
        <WeeklyVideo votw={votw} />
        <SRCCard userCompTeams={userCompTeams} banner={featuredBanners} />
      </Box>
    </Box>
  );
};

export default Dashboard;
